import type { ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Renders the documentation inline syntax: `[label](/href)`, `` `code` ``,
 * and `**strong**`.
 *
 * Content is authored as data, so it is never trusted as HTML — this builds
 * React elements from matched groups and drops everything it does not
 * recognize back out as plain text. There is no `dangerouslySetInnerHTML`
 * anywhere in the docs renderer.
 */
const INLINE = /\[([^\]]+)\]\(([^)\s]+)\)|`([^`]+)`|\*\*([^*]+)\*\*/g;

function isExternal(href: string): boolean {
  return /^https?:\/\//.test(href) || href.startsWith("mailto:");
}

export function renderInline(text: string): ReactNode {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let key = 0;

  // `matchAll` needs the lastIndex reset because INLINE is module-scoped.
  INLINE.lastIndex = 0;
  let match = INLINE.exec(text);
  while (match !== null) {
    if (match.index > cursor) {
      nodes.push(text.slice(cursor, match.index));
    }
    const [, linkLabel, linkHref, code, strong] = match;

    if (linkLabel !== undefined && linkHref !== undefined) {
      nodes.push(
        isExternal(linkHref) ? (
          <a
            key={key++}
            href={linkHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline decoration-primary/40 hover:decoration-primary"
          >
            {linkLabel}
          </a>
        ) : (
          <Link
            key={key++}
            to={linkHref}
            className="text-primary underline decoration-primary/40 hover:decoration-primary"
          >
            {linkLabel}
          </Link>
        ),
      );
    } else if (code !== undefined) {
      nodes.push(
        <code
          key={key++}
          className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-foreground"
        >
          {code}
        </code>,
      );
    } else if (strong !== undefined) {
      nodes.push(
        <strong key={key++} className="font-semibold text-foreground">
          {strong}
        </strong>,
      );
    }

    cursor = match.index + match[0].length;
    match = INLINE.exec(text);
  }

  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }
  return nodes.length === 1 ? nodes[0] : nodes;
}

/** Plain-text projection of the inline syntax, for meta tags and the `.md` twin. */
export function stripInline(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1");
}
